import React, { useState } from 'react';

import infoSvg from '@assets/img/icons/info.svg';
import { Button } from '@components/Button';
import {
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import {
  tradeConditionItems,
  tradeConditionLabelToTags,
  tradeConditionTestQuestions,
  tradeConditionTestResults,
} from '@data/invoice';
import { TradeCondition } from '@models/BookingInquiry';
import { countryStrings } from '@models/Country';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import { isEqual } from 'lodash-es';

const strings = createStrings({
  ja: {
    tradeConditionTest: '貿易条件診断テスト',
    resultTitle: '本件の貿易条件は：',
    CHINA: '以下の中国国内料金の請求先は？',
    D2D: '以下のD2D料金の請求先は？',
    TAX: '日本の輸入関税・輸入消費税の請求先は？',
    CUSTOMS: '税関検査は発生する場合、税関検査料の請求先は？',
    taxExplanation: () => (
      <>
        輸入関税、輸入消費税とは？
        <br />
        海外から日本へ商品を輸入する場合、輸入品に課される税として「関税」と、「消費税（内国消費税と地方消費税）」が課税されます。
        <br />
        ※その他、酒税など特定商品に対して課税されるものもあります。
        <br />
      </>
    ),
  },
});

const resultStrings = createStrings({
  ja: {
    CHINA: '中国料金の請求先は',
    D2D: 'D2D料金の請求先は',
    TAX: '日本関税・輸入消費税の請求先は',
    CUSTOMS: '税関検査料金の請求先は',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contentRoot: {
    height: 450,
    backgroundColor: Color(theme.palette.accent.main).alpha(0.1).toString(),
    paddingTop: 65,
  },
  questionTitle: {
    ...common.biggerText,
    ...common.textAlignCenter,
    marginBottom: 40,
  },
  resultTitle: {
    ...common.normalText,
    ...common.textAlignCenter,
    marginBottom: 34,
  },
  resultLabel: {
    ...common.biggerText,
    ...common.textAccent,
    ...common.textAlignCenter,
    ...common.fontWeight600,
  },
  resultCargosTag: {
    ...common.exSmallerText,
    ...common.textAccent,
    ...common.textAlignCenter,
    ...common.fontWeight600,
    marginBottom: 25,
  },
  resultTradeConditionItems: {
    ...common.flexColumn,
    ...common.itemsCenter,
    ...common.normalText,
    ...common.textBlack,
  },
  resultTradeConditionItem: {
    ...common.flexInline,
    ...common.justifyBetween,
    width: 300,
  },
  actionContainer: {
    ...common.flexInline,
    padding: '1rem 24px',
    columnGap: '1rem',
  },
  countryButton: {
    ...common.normalText,
    ...common.textAccent,
    padding: 20,
    borderRadius: 20,
    border: `1px solid ${theme.palette.accent.main}`,
    '&$selected': {
      ...common.textWhite,
      border: 'initial',
      backgroundColor: theme.palette.accent.main,
    },
  },
  selected: {},
  tradeCondtionItem: {
    ...common.normalText,
    ...common.textBlack,
    width: 200,
  },
  answerContainer: {
    ...common.flexInline,
    ...common.justifyCenter,
    marginTop: 40,
    columnGap: 20,
  },
  taxExplanation: {
    ...common.smallerText,
    ...common.textGray700,
    ...common.flexInline,
    ...common.justifyCenter,
    columnGap: '0.5rem',
    marginTop: 40,
  },
}));

interface CountryButtonProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

const CountryButton: React.FC<CountryButtonProps> = (props) => {
  const { label, selected, onClick } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <button
      onClick={onClick}
      className={clsx(classes.countryButton, selected && classes.selected)}
      type="button"
    >
      {label}
    </button>
  );
};

export interface TradeConditionTestProps {
  hasPickups: 'true' | 'false';
  onGoBack: () => void;
  onChange: (tradeCondition: TradeCondition) => void;
}

export const TradeConditionTest: React.FC<TradeConditionTestProps> = (props) => {
  const { hasPickups, onGoBack, onChange } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [answer, setAnswer] = useState('');

  const [result, setResult] = useState<TradeCondition | undefined>();

  const currentTag = tradeConditionTestQuestions[index];

  const shouldListItems = {
    CHINA: true,
    D2D: true,
    TAX: false,
    CUSTOMS: false,
  };

  const renderQuestion = () => (
    <>
      <div className={classes.questionTitle}>{strings[currentTag]}</div>

      {shouldListItems[currentTag] && (
        <div className={clsx(classes.flexColumn, classes.itemsCenter)}>
          {tradeConditionItems
            .filter(
              (item) =>
                item.tag === currentTag && (item.needsPickup ? hasPickups === 'false' : true),
            )
            .map((item) => (
              <div className={classes.tradeCondtionItem} key={item.name}>
                {item.name}
              </div>
            ))}
        </div>
      )}

      <div className={classes.answerContainer}>
        <CountryButton
          label={countryStrings.cn}
          selected={answer === 'cn'}
          onClick={() => setAnswer('cn')}
        />
        <CountryButton
          label={countryStrings.jp}
          selected={answer === 'jp'}
          onClick={() => setAnswer('jp')}
        />
      </div>

      {currentTag === 'TAX' && (
        <div className={classes.taxExplanation}>
          <div>
            <img src={infoSvg} style={{ height: 14, width: 14 }} alt="Info" />
          </div>

          <div style={{ width: 600 }}>{strings.taxExplanation()}</div>
        </div>
      )}
    </>
  );

  const renderResult = () => {
    const tags = tradeConditionLabelToTags[result!];

    return (
      <>
        <div className={classes.resultTitle}>{strings.resultTitle}</div>

        <div className={classes.resultLabel}>{result}</div>
        <div className={classes.resultCargosTag}>
          {hasPickups === 'false' && bookingInquiryStrings.noPickup}
          {hasPickups === 'true' && bookingInquiryStrings.needsPickup}
        </div>

        <div className={classes.resultTradeConditionItems}>
          {tradeConditionTestQuestions.map((q) => (
            <div className={classes.resultTradeConditionItem} key={q}>
              <div>{resultStrings[q]}</div>

              <div>{tags.cn.includes(q) ? countryStrings.cn : countryStrings.jp}</div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <ActionableDialogTitle>{strings.tradeConditionTest}</ActionableDialogTitle>
      <ActionableDialogContent classes={{ root: classes.contentRoot }}>
        {!result && renderQuestion()}
        {result && renderResult()}
      </ActionableDialogContent>

      <ActionableDialogContentDivider />
      <div className={classes.actionContainer}>
        <Button
          color="accent"
          text={formStrings.prev}
          size="small"
          onClick={onGoBack}
          style={{ width: '100%' }}
          type="button"
        />

        {!result && (
          <Button
            color="accent"
            disabled={!answer}
            text={formStrings.next}
            size="small"
            onClick={() => {
              const nextAnswers = [...answers, answer];

              const result = tradeConditionTestResults.find((r) => isEqual(r.answers, nextAnswers));

              if (result) {
                setResult(result.result);
              } else {
                setIndex(index + 1);
                setAnswers(nextAnswers);
              }

              setAnswer('');
            }}
            style={{ width: '100%' }}
            type="button"
          />
        )}

        {result && (
          <Button
            color="accent"
            text="OK"
            size="small"
            onClick={() => {
              onChange(result);
              onGoBack();
            }}
            style={{ width: '100%' }}
            type="button"
          />
        )}
      </div>
    </>
  );
};
